import {ComGroupIdentity} from '../../models';

import type {MsDocAclManagement, MsDocContentType} from './ms-doc';
import type {MsDocContent} from './content';
import type {AccessType, Acl, Access} from './acl';
import type {Actuality, ActualityStatus} from './actuality';
import type {Background} from './background';
import type {GridData} from './old-grids';
import type {PageRevision} from './revisions';
import type {Group} from './groups';
import type {UserColor} from './colors';
import type {AttachmentsMap, AttachmentsType} from './attachments';
import {AccessRequestInfo} from './access-request';

export type TransformMeta = undefined | {script?: string[]; style?: string[]};

export interface PageBaseFieldsResponse {
    id: number;
    slug: string;
    title: string;
    page_type: PageType;
    active_revision: PageRevision | null;
    html?: string;
    toc?: PageTocItem[];
    meta?: TransformMeta;
}

export interface PageAdditionalFieldsResponse {
    [PageDetailsField.Cluster]: PageClusterResponse;
    [PageDetailsField.Redirect]: PageRedirectResponse | null;
    [PageDetailsField.Authors]: PageAuthors;
    [PageDetailsField.Access]: Access;
    [PageDetailsField.Acl]: Acl;
    [PageDetailsField.Breadcrumbs]: PageBreadcrumbItem[];
    [PageDetailsField.Attributes]: PageAttributes;
    [PageDetailsField.Content]: string | MsDocContent;
    [PageDetailsField.Bookmark]: {id: number} | null;
    [PageDetailsField.Subscription]: {status: PageSubscriptionType} | null;
    [PageDetailsField.Permissions]: UserPermissions[];
    [PageDetailsField.Background]: Background | null;
    [PageDetailsField.LastRevisionId]: number;
    [PageDetailsField.Actuality]: Actuality;
}

interface PageRedirectResponse {
    page_id: number;
    redirect_target: PageBaseFieldsResponse;
}

export interface PageClusterResponse {
    slug: string;
}

export interface PageResponse
    extends PageBaseFieldsResponse,
        Partial<PageAdditionalFieldsResponse> {}

export enum PageType {
    Page = 'page',
    Grid = 'grid',
    CloudPage = 'cloud_page',
    Wysiwyg = 'wysiwyg',
}

export enum PageDetailsField {
    Content = 'content',
    Breadcrumbs = 'breadcrumbs',
    Access = 'access',
    Authors = 'authors',
    Attributes = 'attributes',
    Bookmark = 'bookmark',
    Cluster = 'cluster',
    Subscription = 'subscription',
    Permissions = 'user_permissions',
    Acl = 'acl',
    Redirect = 'redirect',
    Background = 'background',
    LastRevisionId = 'last_revision_id',
    Actuality = 'actuality',
}

export enum StubPageDetailsField {
    Cluster = 'cluster',
    Breadcrumbs = 'breadcrumbs',
    Permissions = 'user_permissions',
    MoveOutPages = 'moved_out_pages',
}

export enum ForbiddenStubPageDetailsField {
    Cluster = 'cluster',
    Breadcrumbs = 'breadcrumbs',
    Authors = 'authors',
    PendingAccessRequest = 'pending_access_request',
    AccessApprovers = 'access_approvers',
    Acl = 'acl',
    Permissions = 'user_permissions',
}

export interface PageBreadcrumbItem {
    id?: number;
    slug: string;
    title: string;
    page_exists: boolean;
}

export interface PageAccess {
    type: AccessType;
    isReadonly: boolean;
    hasExternal: boolean;
    inheritsFrom?: {
        id: number;
        slug: string;
    };
}

export interface PageAttributes {
    created_at: string;
    modified_at: string;
    lang: string;
    is_readonly: boolean;
    comments_count: number;
    comments_enabled: boolean;
    keywords: string[];
    is_collaborative?: boolean;
}

export interface PageActuality {
    status: ActualityStatus;
    markedAt?: string;
    user?: PageUser;
    comment?: string;
    externalLinks?: string[];
    actualPages?: {
        id: number;
        slug: string;
    }[];
}

export interface PageTocItem {
    depth: 1 | 2 | 3 | 4 | 5 | 6;
    anchor: string;
    type: 'heading' | 'womHeading';
    value: string;
}

export interface UserIdentity {
    uid?: string;
    cloud_uid?: string;
}

export interface PageUser {
    id: number;
    identity: UserIdentity;
    username: string;
    display_name: string;
    is_dismissed: boolean;
    color?: UserColor;
}

export interface PageAuthors {
    owner: PageUser;
    last_author: PageUser;
    all: PageUser[];
}

export interface PageMsDocContent {
    aclManagement: MsDocAclManagement;
    iframeSrc: string;
    editSrc: string;
    type: MsDocContentType;
    filename: string;
}

export type PageGridContent = GridData;

export type PageContent = string | PageGridContent | PageMsDocContent;

export type PageTypeContent =
    | {
          pageType: PageType.Page;
          content?: string;
      }
    | {
          pageType: PageType.Grid;
          content?: PageGridContent;
      }
    | {
          pageType: PageType.CloudPage;
          content?: PageMsDocContent;
      }
    | {
          pageType: PageType.Wysiwyg;
          content?: string;
      };

export interface PageBaseProperties {
    id: number;
    slug: string;
    title: string;
    pageType: PageType;
}

export interface PageAcl {
    aclType: AccessType;
    breakInheritance: boolean;
    isReadonly: boolean;
    inheritsFrom?: {
        id: number;
        slug: string;
    };
    users: number[];
    groups: ComGroupIdentity[];
    departments: ComGroupIdentity[];
}

export type ParentAclInfo = {
    aclType: AccessType;
    inheritsFrom?: string;
} | null;

export interface PageRedirect {
    id?: number;
    slug: string;
    title?: string;
    pageType?: PageType;
}

export interface Attachment<T extends AttachmentsType> {
    items: AttachmentsMap[T]['id'][];
    isLoading: boolean;
    isError: boolean;
    nextCursor?: string;
}

export type Attachments = {[K in keyof AttachmentsMap]?: Attachment<K>};

export interface Page extends PageBaseProperties {
    html?: string;
    meta?: TransformMeta;
    content?: PageContent;
    value?: string;
    breadcrumbs?: PageBreadcrumbItem[];
    access?: PageAccess;
    toc?: PageTocItem[];
    owner?: number;
    authors?: number[];
    comments?: number[];
    revision?: PageRevision;
    revisions?: number[];
    revisionsData?: {
        nextCursor?: string;
        loading?: boolean;
        loadingMore?: boolean;
        revision1?: number;
        revision2?: number;
    };
    attributes?: PageAttributes;
    commentsPagesData?: {
        prevCursor: string | null;
        nextCursor: string | null;
    };
    bookmarkId?: number;
    clusterSlug?: string;
    subscription?: PageSubscriptionType;
    subscribersPagesData?: {
        hasNext?: boolean;
        pageId?: number;
    };
    subscribers?: number[];
    userPermissions?: UserPermissions[];
    acl?: PageAcl;
    backgroundId?: number;
    redirect?: PageRedirect;
    lastRevisionId?: number;
    attachments?: Attachments;
    actuality?: PageActuality;
    isDraft?: boolean;
}

export interface MovedOutPage {
    accessible: boolean;
    slug: string;
    title: string;
}

export interface AccessApprovers {
    users: PageUser[];
    groups: Group[];
}

export interface StubPage {
    // base properties
    slug: string;
    title: string;
    breadcrumbs?: PageBreadcrumbItem[];
    clusterSlug?: string;
    userPermissions?: UserPermissions[];

    // only 404 stubs
    movedOutPages?: MovedOutPage[];

    // only for forbidden stubs
    id?: number;
    owner?: number;
    authors?: number[];
    pendingAccessRequest?: AccessRequestInfo;
    accessApprovers?: AccessApprovers;
    acl?: PageAcl;
    isAclLoading?: boolean;
    isAuthorsLoading?: boolean;
    authorsError?: string;
}

export interface PageComment {
    id: number;
    body: string;
    inline_text?: string;
    parent_id: number;
    author: PageUser;
    thread_id?: number;
    created_at: string;
    is_deleted: boolean;
    thread_info?: {
        total_posts: number;
        last_comment: PageComment;
    };
    html?: string;
}

export type PageIdentifier =
    | {
          id: number;
      }
    | {
          slug: string;
      };

export interface PageSubscriber {
    id: number;
    isCluster: boolean;
    user: PageUser;
}

export enum PageSubscriptionType {
    ToPage = 'to_page',
    ToCluster = 'to_cluster',
}

export enum UserPermissions {
    CanCreatePage = 'create_page',
    CanDelete = 'delete',
    CanEdit = 'edit',
    CanView = 'view',
    CanComment = 'comment',
    CanChangeAuthors = 'change_authors',
    CanChangeAcl = 'change_acl',
    IsAdmin = 'admin',
}

export enum PageErrors {
    NotFound = 'NOT_FOUND',
    Forbidden = 'FORBIDDEN',
    General = 'GENERAL',
    RedirectChain = 'REDIRECT_CHAIN',
}

export enum CreatePageErrors {
    DocumentAlreadyExists = 'DOCUMENT_ALREADY_EXISTS',
}

export enum UpdateErrors {
    General = 'GENERAL',
    ContainsSecret = 'CONTAINS_SECRET',
    ChangesConflict = 'CHANGES_CONFLICT',
    MergeFailed = 'MERGE_FAILED',
    MergeError = 'MERGE_ERROR',
    RevisionNotFound = 'REVISION_NOT_FOUND',
}

export type CreatePageCloudData =
    | CreatePageCloudPageEmptyData
    | CreatePageCloudPageFromURLData
    | CreatePageCloudPageUploadDocData
    | CreatePageCloudPageFinalizeUploadData;

export enum CreateCloudPageMethod {
    EmptyDoc = 'empty_doc',
    FromUrl = 'from_url',
    UploadDoc = 'upload_doc',
    FinalizeUpload = 'finalize_upload',
}

interface CreatePageCloudPageEmptyData {
    method: CreateCloudPageMethod.EmptyDoc;
    doctype: MsDocContentType;
}

interface CreatePageCloudPageFromURLData {
    method: CreateCloudPageMethod.FromUrl;
    url: string;
}

export interface CreatePageCloudPageUploadDocData {
    method: CreateCloudPageMethod.UploadDoc;
    mimetype: string;
}

interface CreatePageCloudPageFinalizeUploadData {
    method: CreateCloudPageMethod.FinalizeUpload;
    upload_session: string;
}

export interface RecentCluster {
    id: number;
    slug: string;
    title: string;
}

export interface WikiAutocompleteItem {
    id: string;
    slug: string;
    title: string;
    page_type: PageType;
    grids: WikiAutocompleteGridItem[];
}

interface WikiAutocompleteGridItem {
    id: string;
    title: string;
}

export type EditableSlugError =
    | {
          type: 'slug';
          text: string;
      }
    | {
          type: 'name';
          suggestions?: string[];
          text?: string;
      }
    | {
          type: 'cluster';
          text: string;
      };
